import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Links from "@mui/material/Link";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import Swal from "sweetalert2";
import { Dropdown } from "primereact/dropdown";
import FeaturePicker from "./components/FeaturePicker";
import LoginHeader from "./components/LoginHeader.js";
import AADHeader from "./components/AADHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import { toTreeNodes, comingSoonLabels, selectionFromKeys, keysFromSelection } from "./utils/features";
import "./table.css";

const LOGIN_TYPES = [
  { label: "Microsoft Entra (sign in with a Microsoft work account)", value: "AAD" },
  { label: "Password (email + generated password)", value: "Auth0" },
];
const PLANS = [
  { label: "Trial", value: "trial" },
  { label: "Standard", value: "standard" },
];

const domainOf = (email) => {
  const at = String(email || "").lastIndexOf("@");
  return at > 0 ? email.slice(at + 1).trim().toLowerCase() : "";
};
const plusDays = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Staff form that provisions an organization through POST /admin/orgs/provision.
// Entra orgs are bound to their tenant (resolved from the admin's email domain);
// password orgs get a B2C identity + emailed password minted by the proxy. The
// plan (trial with an end date, or standard) and the module set are chosen here.
export default function OrganizationNew() {
  const username = localStorage.getItem("userName");
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [catalogue, setCatalogue] = useState([]);

  const [name, setName] = useState("");
  const [country, setCountry] = useState(null);
  const [revenue, setRevenue] = useState("");
  const [employees, setEmployees] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [loginType, setLoginType] = useState("AAD");
  const [plan, setPlan] = useState("trial");
  const [planEndsAt, setPlanEndsAt] = useState(plusDays(30));
  const [selection, setSelection] = useState({});

  const [domain, setDomain] = useState("");
  const [domainTouched, setDomainTouched] = useState(false);
  const [tenant, setTenant] = useState(null); // { tenantId, issuer } once resolved
  const [tenantLookup, setTenantLookup] = useState({ busy: false, error: "" });

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const treeNodes = toTreeNodes(catalogue);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [c, f] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}countries`).then((r) => r.json()),
          fetch(`${process.env.REACT_APP_BASE_API_URL}features`).then((r) => r.json()),
        ]);
        setCountries(Array.isArray(c) ? c : (c && c.data) || []);
        const tree = Array.isArray(f) ? f : (f && f.data) || [];
        setCatalogue(tree);
        setSelection(selectionFromKeys(toTreeNodes(tree), ["All"]));
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // Keep the domain in step with the admin email until staff edit it by hand.
  useEffect(() => {
    if (!domainTouched) {
      setDomain(domainOf(adminEmail));
      setTenant(null);
      setTenantLookup({ busy: false, error: "" });
    }
  }, [adminEmail, domainTouched]);

  const isEntra = loginType === "AAD";
  const headers = { "Content-type": "application/json", username };

  async function lookupTenant() {
    if (!domain) return;
    setTenantLookup({ busy: true, error: "" });
    setTenant(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/tenant-lookup?domain=${encodeURIComponent(domain)}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 && data.tenantId) {
        setTenant({ tenantId: data.tenantId, issuer: data.issuer });
        setTenantLookup({ busy: false, error: "" });
      } else {
        setTenantLookup({ busy: false, error: data.error || `No Microsoft tenant is published for ${domain}. Check the domain, or choose the Password login type.` });
      }
    } catch (err) {
      console.error(err);
      setTenantLookup({ busy: false, error: "Unable to reach the server. Please try again." });
    }
  }

  const enabledFeatures = keysFromSelection(treeNodes, selection);
  const canSubmit =
    !loading &&
    name.trim() &&
    country &&
    adminName.trim() &&
    adminEmail.trim() &&
    loginType &&
    enabledFeatures.length > 0 &&
    (plan !== "trial" || planEndsAt) &&
    (!isEntra || (tenant && tenant.tenantId));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    const body = {
      name: name.trim(),
      countries: [country],
      revenue: Number(revenue) || 0,
      numberOfEmployees: Number(employees) || 0,
      adminEmail: adminEmail.trim(),
      adminName: adminName.trim(),
      authenticationType: loginType,
      enabledFeatures,
      plan,
    };
    if (plan === "trial") body.planEndsAt = planEndsAt;
    if (isEntra) {
      body.domain = domain;
      body.tenants = [tenant.tenantId];
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/orgs/provision`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      setLoading(false);
      if (res.status === 200 && data && data.success !== false) {
        const lines = [`<p>${escapeHtml(typeof data.data === "string" ? data.data : `${body.name} created.`)}</p>`];
        lines.push(`<p>Plan: <strong>${plan === "trial" ? `Trial until ${escapeHtml(planEndsAt)}` : "Standard"}</strong> · Modules: ${escapeHtml(enabledFeatures.includes("All") ? "everything" : enabledFeatures.join(", "))}</p>`);
        if (data.identity) {
          lines.push(data.identity.ok
            ? `<p>A sign-in password was generated and emailed to <strong>${escapeHtml(body.adminEmail)}</strong>.</p>`
            : `<p style="color:#b45309">The organization was created, but the password identity could not be set up: ${escapeHtml(data.identity.error || "unknown error")}.</p>`);
        }
        if (data.invite) {
          lines.push(data.invite.ok
            ? `<p>An invite to sign in with Microsoft was emailed to <strong>${escapeHtml(body.adminEmail)}</strong>.</p>`
            : `<p style="color:#b45309">The organization was created, but the invite email failed: ${escapeHtml(data.invite.error || "unknown error")}. Tell the admin to sign in with Microsoft.</p>`);
        }
        navigate("/organizations", { state: { flash: { icon: "success", html: lines.join("") } } });
        return;
      }
      const problems = Array.isArray(data.problems) ? data.problems : [];
      Swal.fire({
        icon: "error",
        title: "Could not provision",
        html: problems.length
          ? `<ul style="text-align:left">${problems.map((p) => `<li>${escapeHtml(p)}</li>`).join("")}</ul>`
          : escapeHtml((data && (data.error || data.data || data.message)) || `Request failed (${res.status}).`),
      });
    } catch (err) {
      setLoading(false);
      console.error(err);
      Swal.fire({ icon: "error", title: "Could not provision", text: "Unable to reach the server. Please try again." });
    }
  }

  const renderHeader = () => {
    const lt = localStorage.getItem("loginType");
    if (lt === "AAD") return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  return (
    <div id="wrapper">
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="steps__containt-hader">
            <h3 className="title">Provision organization</h3>
            <div role="presentation">
              <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon fontSize="small" />}>
                <Links underline="hover" color="#707070" className="breadcrumbs-font">Settings</Links>
                <Link to="/organizations" className="breadcrumbs-font">Organizations</Link>
                <span className="breadcrumbs-font">Provision organization</span>
              </Breadcrumbs>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit} className="form-eco">
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="orgName">Organization name<span className="mark-form">*</span></label>
                    <input id="orgName" type="text" className="form-control" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. BetaHub AI" />
                  </div>
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="orgCountry">Country<span className="mark-form">*</span></label>
                    <Dropdown id="orgCountry" className="form-control wd-100" value={country} onChange={(e) => setCountry(e.value)}
                      options={countries.map((c) => ({ label: c, value: c }))} placeholder="Select country" filter filterPlaceholder="Search country" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="orgRevenue">Revenue</label>
                    <input id="orgRevenue" type="number" min="0" className="form-control" value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder="Optional" />
                  </div>
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="orgEmployees">Employees</label>
                    <input id="orgEmployees" type="number" min="0" className="form-control" value={employees} onChange={(e) => setEmployees(e.target.value)} placeholder="Optional" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="adminName">Administrator name<span className="mark-form">*</span></label>
                    <input id="adminName" type="text" className="form-control" required value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Full name" />
                  </div>
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="adminEmail">Administrator email<span className="mark-form">*</span></label>
                    <input id="adminEmail" type="email" className="form-control" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="name@company.com" pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}" title="Enter a valid email address" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="formlable" htmlFor="loginType">Login type<span className="mark-form">*</span></label>
                    <Dropdown id="loginType" className="form-control wd-100" value={loginType}
                      onChange={(e) => { setLoginType(e.value); setTenant(null); setTenantLookup({ busy: false, error: "" }); }}
                      options={LOGIN_TYPES} placeholder="Select login type" />
                    <small className="form-text text-muted">
                      {isEntra
                        ? "The admin signs in with their Microsoft work account. We bind the organization to their Microsoft tenant."
                        : "A sign-in password is generated and emailed to the admin."}
                    </small>
                  </div>
                  <div className="form-group col-md-3">
                    <label className="formlable" htmlFor="orgPlan">Plan<span className="mark-form">*</span></label>
                    <Dropdown id="orgPlan" className="form-control wd-100" value={plan} options={PLANS} onChange={(e) => setPlan(e.value)} />
                  </div>
                  <div className="form-group col-md-3">
                    <label className="formlable" htmlFor="orgEnds">Trial ends{plan === "trial" && <span className="mark-form">*</span>}</label>
                    <input id="orgEnds" type="date" className="form-control" value={planEndsAt} disabled={plan !== "trial"} onChange={(e) => setPlanEndsAt(e.target.value)} />
                    <small className="form-text text-muted">Runs through this day.</small>
                  </div>
                </div>

                {isEntra && (
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <label className="formlable" htmlFor="orgDomain">Email domain<span className="mark-form">*</span></label>
                      <div className="d-flex" style={{ gap: 8 }}>
                        <input id="orgDomain" type="text" className="form-control" value={domain}
                          onChange={(e) => { setDomainTouched(true); setDomain(e.target.value.trim().toLowerCase()); setTenant(null); }} placeholder="company.com" />
                        <Button type="button" variant="outlined" className="outline-btn" onClick={lookupTenant} disabled={!domain || tenantLookup.busy}
                          sx={{ whiteSpace: "nowrap", borderRadius: "10px", color: "#000" }}>
                          {tenantLookup.busy ? "Looking up…" : "Look up tenant"}
                        </Button>
                      </div>
                      <small className="form-text text-muted">Taken from the administrator email; change it if their organization signs in under a different domain.</small>
                    </div>
                    <div className="form-group col-md-6">
                      <label className="formlable" htmlFor="orgTenant">Microsoft tenant</label>
                      <input id="orgTenant" type="text" className="form-control" readOnly disabled value={tenant ? tenant.tenantId : ""} placeholder="Resolved from the domain" />
                      {tenantLookup.error && <div className="text-danger small mt-1">{tenantLookup.error}</div>}
                      {tenant && <div className="text-success small mt-1">Tenant found. Submitting will onboard it.</div>}
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group col-md-12">
                    <label className="formlable">Modules<span className="mark-form">*</span></label>
                    <FeaturePicker tree={treeNodes} selection={selection} onChange={setSelection} disabled={loading} comingSoon={comingSoonLabels(catalogue)} />
                  </div>
                </div>

                <Button type="submit" className="next-button" variant="contained" size="medium" disabled={!canSubmit}
                  sx={{ color: "#fff", backgroundColor: "#407775", width: "140px", height: "41px", borderRadius: "10px" }}>
                  Provision
                </Button>
                <Button href="/organizations" variant="outlined" size="medium" className="outline-btn"
                  sx={{ color: "#000", marginLeft: "15px", width: "120px", height: "41px", borderRadius: "10px" }}>
                  Cancel
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
